import React, { useEffect, useState } from "react";
import { getImageUrl } from "helpers/Image.helper";
import { getStudentPhotos } from "api/student/v2/student.api";
import LoadingComponent from "commons/loading/Loading.component";
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import { usePopup } from "contexts/PopupContext";
import ImageSlidePopup from "./popup/ImageSlidePopup";

interface StudentPhotosProps {
    studentId: string | undefined;
    isActiveOfPublicProfile: boolean;
    userStudentInfo?: any;
}

const PHOTOS_LIMIT = 10;

const StudentPhotos: React.FC<StudentPhotosProps> = ({ studentId, isActiveOfPublicProfile, userStudentInfo }) => {
    const { showPopup } = usePopup();
    const [isOnLoading, setIsOnLoading] = useState(false);
    const [photos, setPhotos] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [errorMessageForPhotos, setErrorMessageForPhotos] = useState("");

    useEffect(() => {
        if (isActiveOfPublicProfile && studentId) {
            loadPhotos();
        }
    }, [studentId, isActiveOfPublicProfile]);

    const loadPhotos = async (page: number = 1) => {
        if (!studentId) return;

        setIsOnLoading(true);
        try {
            const data = await getStudentPhotos(studentId, page, PHOTOS_LIMIT);
            if (data?.list) {
                setPhotos([...photos, ...data.list]);
                setPage(page);
                setHasMore(data.list.length === PHOTOS_LIMIT);
            }
        } catch (error: any) {
            console.error(error);
            setErrorMessageForPhotos(error.message || "Unknown error occurred while loading photos");
            setHasMore(false);
        } finally {
            setIsOnLoading(false);
        }
    };

    const loadMorePhotos = () => {
        loadPhotos(page + 1);
    };

    const handlePhotoClick = (clickedIndex: number) => {
        // 프로필 사진이 있으면 인덱스를 1 증가시켜야 함
        const adjustedIndex = userStudentInfo?.profileImage ? clickedIndex + 1 : clickedIndex;
        
        showPopup(
            <ImageSlidePopup
                profileImageInfo={userStudentInfo?.profileImage}
                userStudentImageList={photos}
                initialSlideIdx={adjustedIndex}
                userStudentInfo={userStudentInfo}
                onClose={() => { }}
            />
        );
    };

    if (!isActiveOfPublicProfile) {
        return null;
    }

    return (
        <div>
            {errorMessageForPhotos && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-red-600 font-medium">{errorMessageForPhotos}</p>
                    </div>
                </div>
            )}

            {photos && photos.length === 0 && !isOnLoading && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-gray-600 font-medium">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtNoPhotos")}</p>
                </div>
            )}

            {photos && photos.length > 0 && (
                <div className="block relative">
                    <h2 className="text-xl font-bold mb-2 text-gray-800 flex items-center">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfo.txtTitlePhotos")}
                    </h2>
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        {photos.map((photo: any, index: number) => (
                            <div
                                key={index}
                                className="flex-shrink-0 w-24 aspect-square bg-gray-200 overflow-hidden rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 relative cursor-pointer"
                                onClick={() => handlePhotoClick(index)}>
                                <img src={getImageUrl(photo?.imageInfo)} alt={`추가 사진 ${index + 1}`} className="w-full h-full object-cover" />

                                {/* 하단 그라데이션 오버레이 */}
                                <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                                {/* 뷰카운트 */}
                                <div className="absolute bottom-1 left-1 right-1 flex items-center justify-center">
                                    <div className="flex items-center text-white text-xs">
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">{photo?.viewCount || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isOnLoading && <LoadingComponent />}

            {/* {!isOnLoading && hasMore && (
                <div className="mt-4 text-center">
                    <button className="px-4 py-2 text-[#CF3688] border border-[#CF3688] rounded-full text-sm font-medium hover:bg-[#CF3688]/5 transition-colors"
                        onClick={loadMorePhotos}>
                        {getLanguageValue(PAGE_TYPE.COMMON, "btn.txtMore")}
                    </button>
                </div>
            )} */}
        </div>
    );
};

export default StudentPhotos;
